import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { useToast } from '../components/Toast';
import {
  ArrowLeft,
  UtensilsCrossed,
  Bus,
  Milestone,
  ParkingCircle,
  Wrench,
  MoreHorizontal,
  Camera,
  X,
  Loader2,
  MapPin,
} from 'lucide-react';

const EXPENSE_CATEGORIES = [
  { value: 'ALIMENTACAO', label: 'Alimentacao', icon: UtensilsCrossed, color: 'bg-orange-100', iconColor: 'text-orange-600' },
  { value: 'TRANSPORTE', label: 'Transporte', icon: Bus, color: 'bg-blue-100', iconColor: 'text-blue-600' },
  { value: 'PEDAGIO', label: 'Pedagio', icon: Milestone, color: 'bg-purple-100', iconColor: 'text-purple-600' },
  { value: 'ESTACIONAMENTO', label: 'Estacionamento', icon: ParkingCircle, color: 'bg-teal-100', iconColor: 'text-teal-600' },
  { value: 'MANUTENCAO', label: 'Manutencao', icon: Wrench, color: 'bg-red-100', iconColor: 'text-red-600' },
  { value: 'OUTROS', label: 'Outros', icon: MoreHorizontal, color: 'bg-gray-100', iconColor: 'text-gray-600' },
] as const;

const expenseSchema = z.object({
  amount: z.number({ error: 'Informe o valor' }).positive('Valor deve ser maior que 0'),
  description: z.string().optional(),
});

type ExpenseFormData = z.infer<typeof expenseSchema>;

function getCurrentPosition(): Promise<{ latitude: number; longitude: number }> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      reject,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  });
}

export default function ExpenseForm() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ExpenseFormData>({
    resolver: zodResolver(expenseSchema),
  });

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const uploadPhoto = async (file: File): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    return new Promise((resolve, reject) => {
      img.onload = () => {
        const maxWidth = 1280;
        const scale = Math.min(1, maxWidth / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(img.src);

        canvas.toBlob(
          async (blob) => {
            if (!blob) return reject(new Error('Falha ao comprimir imagem'));
            const formData = new FormData();
            formData.append('photo', blob, `expense_${Date.now()}.jpg`);

            try {
              const res = await api.post('/photos/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
              resolve(res.data.data.url);
            } catch (err) {
              reject(err);
            }
          },
          'image/jpeg',
          0.7
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const submitMutation = useMutation({
    mutationFn: async (data: ExpenseFormData) => {
      if (!selectedCategory) throw new Error('Selecione uma categoria');
      if (!photoFile) throw new Error('Tire uma foto do comprovante');

      // Get GPS
      let gps: { latitude: number; longitude: number } | null = null;
      try {
        gps = await getCurrentPosition();
      } catch {
        // GPS optional for expenses
      }

      // Upload photo
      const photo_url = await uploadPhoto(photoFile);

      const payload: Record<string, unknown> = {
        category: selectedCategory,
        amount: data.amount,
        description: data.description || undefined,
        photo_url,
      };
      if (gps) {
        payload.latitude = gps.latitude;
        payload.longitude = gps.longitude;
      }

      const res = await api.post('/expenses', payload);
      return res.data.data;
    },
    onSuccess: () => {
      toast({ title: 'Despesa registrada com sucesso', variant: 'success' });
      setLocation('/');
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error?.message || error.message || 'Erro ao registrar despesa';
      toast({ title: msg, variant: 'error' });
    },
  });

  const onSubmit = (data: ExpenseFormData) => {
    if (!selectedCategory) {
      toast({ title: 'Selecione uma categoria', variant: 'warning' });
      return;
    }
    if (!photoFile) {
      toast({ title: 'Tire uma foto do comprovante', variant: 'warning' });
      return;
    }
    submitMutation.mutate(data);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setLocation('/')}
            className="p-2 -ml-2 rounded-full active:bg-white/20"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-lg font-bold">Registrar Despesa</h1>
        </div>
      </header>

      <main className="flex-1 p-4 space-y-4">
        {/* Category Grid */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Categoria *</label>
          <div className="grid grid-cols-3 gap-3">
            {EXPENSE_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isSelected = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors active:scale-95 ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-100 bg-white'
                  }`}
                >
                  <div className={`p-2 rounded-full ${cat.color}`}>
                    <Icon className={`h-5 w-5 ${cat.iconColor}`} />
                  </div>
                  <span className={`text-xs font-medium ${isSelected ? 'text-primary' : 'text-gray-700'}`}>
                    {cat.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Amount */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
            Valor (R$) *
          </label>
          <input
            id="amount"
            type="number"
            inputMode="decimal"
            step="0.01"
            {...register('amount', { valueAsNumber: true })}
            className={`w-full px-3 py-3 rounded-xl border text-lg font-semibold bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
              errors.amount ? 'border-red-300' : 'border-gray-200'
            }`}
            placeholder="0,00"
          />
          {errors.amount && (
            <p className="text-xs text-red-500 mt-1">{errors.amount.message}</p>
          )}
        </div>

        {/* Description */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Descricao (opcional)
          </label>
          <textarea
            id="description"
            {...register('description')}
            rows={3}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors resize-none"
            placeholder="Detalhes sobre a despesa..."
          />
        </div>

        {/* Photo */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Foto do Comprovante *
          </label>
          {photoPreview ? (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Comprovante"
                className="w-full h-40 object-cover rounded-xl border border-gray-200"
              />
              <button
                type="button"
                onClick={() => {
                  setPhotoPreview(null);
                  setPhotoFile(null);
                }}
                className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full text-white"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 active:bg-gray-50"
            >
              <Camera className="h-8 w-8" />
              <span className="text-sm font-medium">Tirar foto do comprovante</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoCapture}
            className="hidden"
          />
        </div>

        {/* GPS info */}
        <div className="flex items-center gap-2 px-2 text-xs text-gray-400">
          <MapPin className="h-3.5 w-3.5" />
          <span>Localizacao sera capturada automaticamente</span>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit(onSubmit)}
          disabled={submitMutation.isPending}
          className="w-full flex items-center justify-center gap-2 p-4 bg-primary text-white rounded-xl font-semibold active:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitMutation.isPending ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Enviando...
            </>
          ) : (
            'Registrar Despesa'
          )}
        </button>
      </main>
    </div>
  );
}
